import debug from 'debug';
import Map from 'ol/Map';
import Overlay from 'ol/Overlay';
import { withInteraction } from '../functions';
import type { IMark, InteractionMark, MarkOptions } from '../types';

const logger = debug('sdi:map/mark');

type IdleMark = { tag: 'idle' };
type ActiveMark = { tag: 'active'; overlay: Overlay };
type MarkState = IdleMark | ActiveMark;

const idle: MarkState = { tag: 'idle' };
const active = (overlay: Overlay): MarkState => ({ tag: 'active', overlay });

const markerOverlay = (state: IMark) => {
    const marker = document.createElement('div');
    marker.setAttribute('class', 'map-marker');

    const overlay = new Overlay({
        positioning: 'center-center',
        element: marker,
    });
    overlay.setPosition(state.coordinates);
    return overlay;
};

/**
 * @param startMark - Triggered on the first update. Set the mark Interaction in this function
 * @param endMark   - Triggered on singleclick event and on each update once the state is started.
 *                    Set the next map Interaction in this function.
 * @returns An object with:
 *   - `init(map)` – registers the overlay and attaches the click listener.
 *   - `update()`  – drives per-frame state transitions
 *
 */
export const mark = ({ startMark, endMark }: MarkOptions) => {
    let mapRef: Map;
    let markState: MarkState = idle;

    const setup = (state: IMark) => {
        logger('setup mark interaction');

        const overlay = markerOverlay(state);
        mapRef.addOverlay(overlay);
        mapRef.on('singleclick', endMark);
        markState = active(overlay);
        startMark();
    };

    const updateMark = (interaction: IMark, activeMark: ActiveMark) => {
        const hasExpired = interaction.endTime <= Date.now();
        if (hasExpired) {
            endMark();
        } else {
            activeMark.overlay.setPosition(interaction.coordinates);
        }
    };

    const teardown = () => {
        if (markState.tag === 'active') {
            logger('tear down marker');
            mapRef.un('singleclick', endMark);
            mapRef.removeOverlay(markState.overlay);
            markState = idle;
        }
    };

    const update = withInteraction<InteractionMark>(
        'mark',
        ({ state }) => {
            logger('update', state.started, state.coordinates);
            if (state.started && markState.tag === 'active')
                updateMark(state, markState);
            else if (!state.started) {
                setup(state);
            }
        },
        teardown,
    );

    const init = (map: Map) => {
        mapRef = map;
    };
    return { init, update };
};

logger('loaded');
